import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  distinctUntilChanged,
  type Observable,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import type { EduBudgetCorrection } from '../../shared/models/api/edu-budget-correction.model';
import type { Page } from '../../shared/models/api/page.model';
import type { User } from '../../shared/models/api/user.model';
import { FillArrayPipe } from '../../shared/pipes/fill-array.pipe';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { EduBudgetCorrectionService } from '../../shared/services/api/edu-budget-correction.service';
import { MediaQueryService } from '../../shared/services/media-query.service';

@Component({
  selector: 'opa-edu-budget-correction-list',
  imports: [
    CommonModule,
    OpaCurrencyPipe,
    PorscheDesignSystemModule,
    RouterLink,
    LetDirective,
    FillArrayPipe,
  ],
  templateUrl: './edu-budget-correction-list.component.html',
  styleUrl: './edu-budget-correction-list.component.scss',
})
export class EduBudgetCorrectionListComponent {
  private readonly eduBudgetCorrectionService = inject(EduBudgetCorrectionService);
  private readonly mediaQueryService = inject(MediaQueryService);

  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  private readonly pageNo = new BehaviorSubject(1);
  private readonly loading = new BehaviorSubject(false);
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  eduBudgetCorrections$ = this.getEduBudgetCorrections$();

  reloadEduBudgetCorrections(): void {
    this.eduBudgetCorrections$ = this.getEduBudgetCorrections$();
  }

  trackRow(index: number): number {
    return index;
  }

  trackUser(index: number, user: User): number {
    return user.id;
  }

  updatePageNo(page: number): void {
    this.pageNo.next(page);
  }

  private getEduBudgetCorrections$(): Observable<Page<EduBudgetCorrection>> {
    return this.pageNo.pipe(
      distinctUntilChanged(),
      tap(() => this.loading.next(true)),
      switchMap((pageNo) => this.eduBudgetCorrectionService.getEduBudgetCorrections(pageNo)),
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
